import { Caption, VButton, VTooltip, VTooltipContent, VTooltipTrigger } from '@vrzl/ui';
import { useSetAtom } from 'jotai';
import { FileText } from 'lucide-react';
import type { ReactNode } from 'react';

import { documentDialogAtom } from '@/domains/documents';

import { type AnswerSource, pagesLabel } from '../utils/sources';

interface SourceButtonProps {
  eventId: string;
  criterionId: string;
  source: AnswerSource;
  highlightQuery: string | undefined;
}

/**
 * Eén document: icoon met de pagina's ("p. 8, 9, 12"). De volledige naam staat in de
 * tooltip (hover en focus); klikken opent het document op die pagina's.
 */
export function SourceButton({ eventId, criterionId, source, highlightQuery }: SourceButtonProps) {
  const setDocDialog = useSetAtom(documentDialogAtom);
  const pages = pagesLabel(source.pages);
  const ariaLabel =
    source.pages.length === 0
      ? `Open ${source.docName}`
      : `Open ${source.docName}, pagina ${source.pages.join(', ')}`;

  return (
    <VTooltip>
      <VTooltipTrigger asChild>
        <VButton
          type="button"
          variant="ghost"
          size="sm"
          icon={FileText}
          aria-label={ariaLabel}
          className="h-auto min-h-7 gap-1 px-1.5 py-1 text-left font-normal text-muted-foreground"
          onClick={() =>
            setDocDialog({
              eventId,
              docId: source.docId,
              initialPages: source.pages.length === 0 ? undefined : source.pages,
              highlightQuery,
              initialEvidence: { criterionId, evidence: source.evidence },
            })
          }
        >
          {pages}
        </VButton>
      </VTooltipTrigger>
      <VTooltipContent className="wrap-break-word">
        <Caption className="font-medium text-foreground">{source.docName}</Caption>
        {pages && <Caption>{pages}</Caption>}
      </VTooltipContent>
    </VTooltip>
  );
}

interface SourcedValueProps {
  eventId: string;
  criterionId: string;
  sources: AnswerSource[];
  /** Gevonden waarde; de PDF-viewer markeert deze tekst op de pagina. */
  highlightQuery: string | undefined;
  children: ReactNode;
}

/**
 * Eén waarde over de volle breedte, met daaronder één bron per document.
 * Tooltips printen niet, dus op papier staan de bronnen voluit.
 */
export function SourcedValue({
  eventId,
  criterionId,
  sources,
  highlightQuery,
  children,
}: SourcedValueProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="min-w-0 wrap-break-word">{children}</div>
      {sources.length > 0 && (
        <>
          <div className="-ml-1.5 flex flex-wrap gap-0.5 print:hidden">
            {sources.map((source) => (
              <SourceButton
                key={source.docId}
                eventId={eventId}
                criterionId={criterionId}
                source={source}
                highlightQuery={highlightQuery}
              />
            ))}
          </div>
          <ul className="hidden print:block">
            {sources.map((source) => (
              <li key={source.docId}>
                <Caption>
                  {source.pages.length === 0
                    ? source.docName
                    : `${source.docName} · ${pagesLabel(source.pages)}`}
                </Caption>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
